import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {ApiService} from '../../../core/http/api.service';
import {AddressDetailsModel} from '../../models/addresses/addresses.models';
import {
  FIELD_MANDATORY_ERROR,
  SELECT_ADDRESS_FILL_MANUALLY_ERROR,
  SHORTER_POSTCODE_ERROR,
  VALID_POSTCODE_ERROR
} from '../../../core/constants/validation-error-strings.constants';
import {Subscription} from 'rxjs';
import {PostcodeLookupService} from '../../services/postcode-lookup.service';

@Component({
  selector: 'app-postcode-lookup',
  templateUrl: './postcode-lookup.component.html',
  styleUrls: ['./postcode-lookup.component.scss']
})
export class PostcodeLookupComponent implements OnInit, OnDestroy {
  readonly VALID_POSTCODE_ERROR: string = VALID_POSTCODE_ERROR;
  readonly FIELD_MANDATORY_ERROR: string = FIELD_MANDATORY_ERROR;
  readonly SHORTER_POSTCODE_ERROR: string = SHORTER_POSTCODE_ERROR;
  readonly SELECT_ADDRESS_FILL_MANUALLY_ERROR: string = SELECT_ADDRESS_FILL_MANUALLY_ERROR;

  @Input() formSubmitted: boolean;
  @Input() inputForm: UntypedFormGroup;
  @Input() manualPostcodeInput: boolean;
  @Input() customFormName = 'postcodeAddress';
  @Input() customPostcodeName = 'postcode';
  @Input() customTownName = 'town';
  @Input() customAddressName = 'address1';
  @Input() customHouseNameNumberName = 'houseNameNumber';

  addressList: AddressDetailsModel[];
  addressListForToDisplay;
  clickedFindAddress: boolean;
  isLoading = false;

  private clickedFindAddressEmitSubscriber: Subscription;

  get houseNameNumber(): AbstractControl | null {
    return this.inputForm?.get(`${this.customFormName}.${this.customHouseNameNumberName}`);
  }

  get address1(): AbstractControl | null {
    return this.inputForm?.get(`${this.customFormName}.${this.customAddressName}`);
  }

  get town(): AbstractControl | null {
    return this.inputForm?.get(`${this.customFormName}.${this.customTownName}`);
  }

  get postcode(): AbstractControl | null {
    return this.inputForm?.get(`${this.customFormName}.${this.customPostcodeName}`);
  }

  get postcodeAddressFormGroup(): AbstractControl | null {
    return this.inputForm?.get(this.customFormName);
  }

  get selectedAddressId(): AbstractControl | null {
    return this.inputForm?.get(`${this.customFormName}.selectedAddressId`);
  }

  get showAddressListSelect(): boolean {
    return Boolean(this.addressList && this.selectedAddressId);
  }

  get showAddressListSelectValidationError(): boolean {
    return Boolean(this.postcode?.valid && this.postcodeAddressFormGroup.invalid && this.formSubmitted);
  }

  constructor(private api: ApiService,
              private postcodeLookupService: PostcodeLookupService) {
  }

  ngOnInit(): void {
    this.clickedFindAddressEmitSubscriber = this.postcodeLookupService.clickedFindAddressEmit.subscribe((status) => {
      this.manualPostcodeInput = status;
      this.toggleAddressSelectInput(status);
    });
  }

  ngOnDestroy(): void {
    this.clickedFindAddressEmitSubscriber.unsubscribe();
  }

  /**
   * Triggers on 'Find postcode' button is pressed
   * Checks postcode field validity and hits api with it
   * Uses response to generate selection list
   */
  getPostcode(): void {
    this.isLoading = true;
    this.clickedFindAddress = true;

    if (this.postcode?.valid) {
      this.api.Addresses.getPostcodeDetails(this.postcode.value)
        .subscribe((responseData) => {
          this.generateAddressList(responseData.data);
          this.toggleAddressSelectInput(true);
          this.isLoading = false;
        }, () => {
          this.postcode.setErrors({required: true});
          this.isLoading = false;
        });
    } else {
      this.isLoading = false;
    }
  }

  /**
   * Triggers once address list select item is selected
   */
  selectedAddress(): void {
    const valuesToUse = this.addressList[this.selectedAddressId.value];
    this.fillAddressFields(valuesToUse.houseName, valuesToUse.street, valuesToUse.town, valuesToUse.postcode);
    this.toggleAddressSelectInput(false);
  }

  /**
   * Generates address list for address select options
   * @param initialAddressList: AddressDetailsModel[]
   * @private
   */
  private generateAddressList(initialAddressList: AddressDetailsModel[]): void {
    this.addressList = initialAddressList;
    this.addressListForToDisplay = initialAddressList.map((addressDetail, index) => {
      return {
        id: index,
        label: `${addressDetail.houseName}, ${addressDetail.street}, ${addressDetail.town}`
      };
    });
  }

  /**
   * Adds or removes selectedAddressId select input form group
   * @param shouldAdd: boolean
   * @private
   */
  private toggleAddressSelectInput(shouldAdd: boolean): void {
    const abstractControl: AbstractControl = this.postcodeAddressFormGroup;

    if (abstractControl instanceof UntypedFormGroup) {
      if (shouldAdd) {
        abstractControl.addControl('selectedAddressId', new UntypedFormControl(''));
      } else {
        abstractControl.removeControl('selectedAddressId');
      }
    }
  }

  /**
   * Fills passed values as address form details
   * @param houseNumber: string
   * @param address1: string
   * @param town: string
   * @param postcode: string
   * @private
   */
  private fillAddressFields(houseNumber: string, address1: string, town: string, postcode: string): void {
    this.manualPostcodeInput = true;
    this.houseNameNumber.setValue(houseNumber);
    this.address1.setValue(address1);
    this.town.setValue(town);
    this.postcode.setValue(postcode);
  }
}
